<script lang="ts">
  import { Book, CircleHelpIcon, SlackIcon } from "lucide-svelte";
  import { createEventDispatcher } from "svelte";

  import logo from "$lib/assets/logo.svg";
  import UserMenu from "$lib/ui/common/UserMenu.svelte";
  import UnderlinedTab from "$lib/ui/system/UnderlinedTab.svelte";
  import UnderlinedTabList from "$lib/ui/system/UnderlinedTabList.svelte";
  import EnvironmentPicker from "./internal/EnvironmentPicker.svelte";
  import WorkspacePicker from "./internal/WorkspacePicker.svelte";
  import LiveUsageDropdown from "./usage/LiveUsageDropdown.svelte";
  import AdminOnly from "../common/AdminOnly.svelte";
  import LoginSignupButtons from "../common/LoginSignupButtons.svelte";
  import Faq from "../playground/Faq.svelte";
  import Popup from "../system/Popup.svelte";

  // Whether this is a workspace-specific navbar with links, or an internal page.
  export let workspace: string | null = null;
  export let environment: string | null = null;
  export let isPlayground: boolean = false;

  let faqDialogOpen = false;

  const dispatch = createEventDispatcher<{
    switchWorkspace: string;
    switchEnv: string;
  }>();
  // workspace is not always specified in the URL (e.g. internal pages)
  $: prefix =
    workspace && environment
      ? `/${workspace}/${encodeURIComponent(environment)}`
      : "";

  $: links = [
    { to: `/apps${prefix}`, name: "Apps" },
    { to: `/logs${prefix}`, name: "Logs" },
    { to: `/secrets${prefix}`, name: "Secrets" },
    { to: `/storage${prefix}`, name: "Storage" },
    { to: `/sandboxes${prefix}`, name: "Sandboxes" },
  ];
</script>

<header class="border-b border-white/10 flex justify-center px-4">
  <div class="w-full">
    <!-- pb-3 is only used for the internal page navbar, because it doesn't have a second row underneath -->
    <div
      class="flex flex-wrap sm:flex-nowrap sm:items-center px-2 gap-x-8 gap-y-3 py-3"
    >
      <slot name="logo">
        <a href="{prefix}/apps" class="flex-shrink-0 h-10 flex items-center">
          <img src={logo} alt="Modal" class="h-5 my-auto" />
        </a>
      </slot>
      {#if workspace && environment}
        <div
          class="order-last basis-full sm:order-none sm:basis-auto flex items-center gap-x-1.5 min-w-0"
        >
          <WorkspacePicker
            {workspace}
            on:switch={(event) => {
              if (isPlayground) {
                event.preventDefault();
                dispatch("switchWorkspace", event.detail);
              }
            }}
          />
          <span class="text-xl text-white/20 font-medium">/</span>
          <EnvironmentPicker
            {workspace}
            {environment}
            on:switch={(event) => {
              if (isPlayground) {
                event.preventDefault();
                dispatch("switchEnv", event.detail);
              }
            }}
          />
        </div>
      {/if}
      <div class="ml-auto shrink-0 flex items-center">
        {#if workspace}
          <div class="mr-2 md:mr-4">
            <LiveUsageDropdown {workspace} />
          </div>
        {/if}

        {#if isPlayground}
          <button
            class="btn p-2 hidden sm:flex md:py-1 gap-x-2 items-center"
            on:click={() => (faqDialogOpen = true)}
          >
            <CircleHelpIcon size={16} class="text-zinc-500" />
            <span class="hidden md:inline text-zinc-400 text-sm font-medium"
              >FAQ</span
            >
          </button>
        {/if}

        <a
          href="/slack"
          target="_blank"
          rel="noopener noreferrer"
          class="btn p-2 md:py-1 flex gap-x-2 items-center"
        >
          <SlackIcon size={16} class="text-zinc-500" />
          <span class="hidden md:inline text-zinc-400 text-sm font-medium"
            >Slack Community</span
          >
        </a>

        <a
          href="/docs"
          target="_blank"
          rel="noopener noreferrer"
          class="ml-2 mr-4 btn p-2 md:py-1 flex space-x-2 items-center"
        >
          <Book size={16} class="text-zinc-500" />
          <span class="hidden md:inline text-zinc-400 text-sm font-medium"
            >Docs</span
          >
        </a>
        {#if isPlayground && !workspace}
          <LoginSignupButtons next="/playground" />
        {:else}
          <UserMenu />
        {/if}
      </div>
    </div>

    {#if !isPlayground && workspace}
      <div class="overflow-auto pb-[1px] translate-y-[1px] -mt-3">
        <UnderlinedTabList class="border-none overflow-visible px-2">
          {#each links as { to, name }}
            <UnderlinedTab href={to}>{name}</UnderlinedTab>
          {/each}
          <AdminOnly>
            <UnderlinedTab href={`/containers${prefix}`}
              >Containers</UnderlinedTab
            >
          </AdminOnly>
        </UnderlinedTabList>
      </div>
    {/if}
  </div>
</header>

{#if isPlayground}
  <Popup
    title="Playground FAQ"
    size="lg"
    open={faqDialogOpen}
    on:close={() => (faqDialogOpen = false)}
    showCloseButton
  >
    <Faq />
  </Popup>
{/if}
